import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { VehicleType } from "@enums/VehicleType.enum";
import { IDriver } from "@interfaces/IDriver";
import { IVehicle } from "@interfaces/IVehicle";
import { DriverService } from "@services/driver.service";
import { DriverAssignmentService } from "@services/driverAssignment.service";
import { TachoService } from "@services/tacho.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import * as moment from "moment";
import "./driverSummaryPanel.component.scss";

@Component({
    selector: 'driver-summary-panel',
    template: require('./driverSummaryPanel.component.html')
})
export class DriverSummaryPanelComponent implements OnChanges {
    @Input('driver') originalDriver: IDriver;
    @Input('vehicle') originalVehicle: IVehicle;
    @Input() time: moment.Moment;
    @Input() compactView = false;
    @Input() hideVehicleReg: boolean = false;
    @Input() showTbcs: boolean;
    @Input() showPhone = false;
    @Input() showTacho = false;
    @Input() lightText = false;

    driver: IDriver;
    vehicle: IVehicle;

    vehicleAssignedFromBinding: boolean;

    vehicleTBC = false;
    logging = false;

    constructor(private vehicleService: VehicleService,
        private driverService: DriverService,
        private driverAssignmentService: DriverAssignmentService,
        private tachoService: TachoService,
        private translationService: TranslationService) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.originalDriver) {
            this.driver = this.originalDriver;
        }

        if (changes.originalVehicle) {
            this.vehicle = this.originalVehicle;
        }

        // Passing specifically null in means "use now", undefined means dont use time
        if (this.time === null) {
            if (this.logging) {
                console.log("time null");
            }

            this.time = moment();
        }

        if (this.time) {
            if (typeof this.time == "string") {
                this.time = moment(this.time);
            }

            var timeChanged = changes.time && changes.time.currentValue != changes.time.previousValue;

            if (timeChanged) {
                this.updateDriver();
                this.updateVehicle();
            }
        }
        else {
            if (!this.isRealDriver(this.driver)) {
                const label = this.vehicle && this.vehicleService.isNonDrivableVehicleType(this.vehicle)
                    ? this.translationService.translateEnum(VehicleType, "VehicleType", this.vehicle.VehicleType)
                    : this.translationService.t('DriverSummaryPanel.NoDriver') /*+ this.vehicle.VehicleType*/;

                this.setDriverLabel(label);
            }

            // Added by CJE 24.8.2018 to address "Blank reg" problem
            if (this.vehicle == null) {
                this.setVehicleLabel("TBC");
            }
        }

        this.updateDriverTacho();
    }

    //------------

    private updateDriverTacho() {
        const timeCopy = this.time ? this.time.clone() : moment();

        if (!this.showTacho || !this.driver || !this.vehicle || !timeCopy.isSame(moment(), "d")) {

            return;
        }

        this.driverService
            .getDriversTacho()
            .then(tacho => {
                this.tachoService.mapDriverToTachoObjects(this.driver, tacho);
            });
    }

    private updateDriver() {

        if (this.originalDriver) {
            this.driver = this.originalDriver;
            return;
        }

        var sanitizedTime = this.time || moment();

        if (this.showTbcs) {
            this.setDriverLabel("TBC");
            return;
        }

        if (!this.vehicle || !this.vehicle.Id) {
            this.setDriverLabel("Unkno-1");
            return;
        }

        this.driverAssignmentService
            .getDriverAssignmentForTime(this.originalVehicle.Id, sanitizedTime)
            .then(historicalDriver => {
                this.driver = historicalDriver;

                this.setDriverLabel(this.translationService.t('DriverSummaryPanel.NoDriver'));

                this.updateDriverTacho();
            });
    }

    private updateVehicle() {

        if (this.originalVehicle) {

            this.vehicle = this.originalVehicle;
            return;
        }

        var sanitizedTime = this.time || moment();

        if (this.showTbcs) {
            this.setVehicleLabel("TBC");
            return;
        }

        if (!this.originalDriver || !this.originalDriver.Id || this.vehicleAssignedFromBinding) {
            this.setVehicleLabel("---");
            return;
        }

        if (this.logging) {
            console.warn("updateVehicleForTime - nigel wood", this.originalDriver.Id, sanitizedTime.toISOString(), this.time.toISOString());
        }

        this.driverAssignmentService
            .getAssignmentByDriverForTime(this.originalDriver.Id, sanitizedTime, this.logging)
            .then(historicalVehicle => {

                this.vehicle = historicalVehicle;

                if (!this.vehicle) {

                    this.vehicle = { Reg: this.translationService.t('Common.Unknown'), ShortReg: this.translationService.t('Common.Unknown') };
                }
            });
    }

    private formatTime(): void {
        if (this.time) {
            this.time = moment(this.time);
        }
        else {
            this.time = moment();
        }
    }

    private setDriverLabel(label: string) {
        if (this.isRealDriver(this.driver)) {
            return;
        }

        this.driver = { Id: 0, Name: label };
    }

    private setVehicleLabel(label: string) {
        if (this.vehicle) {
            return;
        }

        this.vehicle = { Reg: label, ShortReg: label };
    }

    private isRealDriver(driver: IDriver) {
        return driver && driver.Id > 0;
    }
}
